import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  isCognitoConfigured,
  userManager,
  loginRedirect,
  logoutRedirect,
  getActiveUser,
} from './auth';
import { identifyUser } from './analytics';

const AuthContext = createContext({
  user: null,
  loading: true,
  isConfigured: false,
  login: () => {},
  logout: () => {},
});

function flattenUser(u) {
  if (!u) return null;
  const p = u.profile || {};
  return {
    sub: p.sub,
    email: p.email,
    name: p.name || p['cognito:username'] || p.email,
    picture: p.picture,
    expired: u.expired,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(isCognitoConfigured);

  const refresh = useCallback(async () => {
    if (!isCognitoConfigured) {
      setLoading(false);
      return;
    }
    const u = await getActiveUser();
    setUser(flattenUser(u));
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    if (!userManager) return undefined;
    const onLoaded = (u) => setUser(flattenUser(u));
    const onUnloaded = () => setUser(null);
    const onExpired = () => setUser((prev) => (prev ? { ...prev, expired: true } : prev));
    userManager.events.addUserLoaded(onLoaded);
    userManager.events.addUserUnloaded(onUnloaded);
    userManager.events.addAccessTokenExpired(onExpired);
    return () => {
      userManager.events.removeUserLoaded(onLoaded);
      userManager.events.removeUserUnloaded(onUnloaded);
      userManager.events.removeAccessTokenExpired(onExpired);
    };
  }, [refresh]);

  const sub = user?.sub;
  useEffect(() => {
    if (loading) return;
    identifyUser(user);
    // Re-identify only when the account changes, not on token refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub, loading]);

  const value = {
    user,
    loading,
    isConfigured: isCognitoConfigured,
    login: loginRedirect,
    logout: logoutRedirect,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
